import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';

interface QuotaAdjustment {
  id: string;
  admin_id: string;
  user_id: string;
  adjustment_type: string;
  adjustment_amount: number | null;
  previous_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  reason: string;
  created_at: string;
  admin_email?: string;
  user_email?: string;
}

interface QuotaAuditLogProps {
  userId?: string; // Optional: filter by specific user
  limit?: number;
}

export default function QuotaAuditLog({ userId, limit = 50 }: QuotaAuditLogProps) {
  const [adjustments, setAdjustments] = useState<QuotaAdjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('quota_adjustments')
        .select(`
          *,
          admin:admin_id(email),
          user:user_id(email)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.log('Quota audit log not available:', fetchError);
        setError('Database schema not deployed. Showing preview audit data.');
        
        // Show preview data
        setAdjustments([
          {
            id: '1',
            admin_id: 'admin-1',
            user_id: 'user-1',
            adjustment_type: 'credit_add',
            adjustment_amount: 50,
            previous_value: { quota: 25, plan_type: 'junior_sdr' },
            new_value: { quota: 75, plan_type: 'junior_sdr' },
            reason: 'Customer requested additional credits',
            created_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
            admin_email: 'jd@aipenguins.com',
            user_email: 'user@example.com'
          },
          {
            id: '2',
            admin_id: 'admin-1',
            user_id: 'user-2',
            adjustment_type: 'plan_change',
            adjustment_amount: null,
            previous_value: { quota: 25, plan_type: 'junior_sdr' },
            new_value: { quota: 150, plan_type: 'top_qualifier_sdr' },
            reason: 'Upgraded to Top Qualifier plan',
            created_at: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            admin_email: 'jd@aipenguins.com',
            user_email: 'john@company.com'
          },
          {
            id: '3',
            admin_id: 'admin-1',
            user_id: 'user-3',
            adjustment_type: 'quota_reset',
            adjustment_amount: null,
            previous_value: { quota: 2, plan_type: 'junior_sdr' },
            new_value: { quota: 25, plan_type: 'junior_sdr' },
            reason: 'Monthly quota reset',
            created_at: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
            admin_email: 'jd@aipenguins.com',
            user_email: 'mary@startup.com'
          }
        ]);
      } else {
        // Transform the data to include admin and user emails
        const transformedData = data?.map(adjustment => ({
          ...adjustment,
          admin_email: adjustment.admin?.email || 'Unknown',
          user_email: adjustment.user?.email || 'Unknown'
        })) || [];
        
        setAdjustments(transformedData);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError('Failed to load audit log data');
    } finally {
      setLoading(false);
    }
  }, [userId, limit]);

  useEffect(() => {
    fetchAuditLog();
  }, [userId, fetchAuditLog]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAdjustmentTypeLabel = (type: string) => {
    switch (type) {
      case 'credit_add':
        return 'Added Credits';
      case 'credit_subtract':
        return 'Subtracted Credits';
      case 'credit_set':
        return 'Set Credits';
      case 'plan_change':
        return 'Changed Plan';
      case 'quota_reset':
        return 'Reset Quota';
      default:
        return type;
    }
  };

  const getAdjustmentTypeBadge = (type: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
    
    switch (type) {
      case 'credit_add':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'credit_subtract':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'credit_set':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'plan_change':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'quota_reset':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatQuotaChange = (adjustment: QuotaAdjustment) => {
    const previousQuota = adjustment.previous_value?.export_quota_remaining || 0;
    const newQuota = adjustment.new_value?.export_quota_remaining || 0;
    
    switch (adjustment.adjustment_type) {
      case 'credit_add':
        return `${previousQuota} → ${newQuota} (+${adjustment.adjustment_amount})`;
      case 'credit_subtract':
        return `${previousQuota} → ${newQuota} (-${adjustment.adjustment_amount})`;
      case 'credit_set':
        return `${previousQuota} → ${newQuota}`;
      case 'plan_change': {
        const previousPlan = adjustment.previous_value?.plan_type === 'top_qualifier_sdr' ? 'Top Qualifier' : 'Junior SDR';
        const newPlan = adjustment.new_value?.plan_type === 'top_qualifier_sdr' ? 'Top Qualifier' : 'Junior SDR';
        return `${previousPlan} → ${newPlan} (${previousQuota} → ${newQuota})`;
      }
      case 'quota_reset':
        return `${previousQuota} → ${newQuota} (reset)`;
      default:
        return `${previousQuota} → ${newQuota}`;
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Quota Adjustment History
          </h3>
          <button
            onClick={fetchAuditLog}
            disabled={loading}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {userId && (
          <p className="text-sm text-gray-600 mt-1">
            Showing adjustments for selected user
          </p>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Change
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Admin
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span className="text-gray-500">Loading audit log...</span>
                  </div>
                </td>
              </tr>
            ) : adjustments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="text-gray-500">No quota adjustments found.</div>
                </td>
              </tr>
            ) : (
              adjustments.map((adjustment) => (
                <tr key={adjustment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(adjustment.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {adjustment.user_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getAdjustmentTypeBadge(adjustment.adjustment_type)}>
                      {getAdjustmentTypeLabel(adjustment.adjustment_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatQuotaChange(adjustment)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={adjustment.reason}>
                      {adjustment.reason}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {adjustment.admin_email}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          Showing {adjustments.length} recent adjustment{adjustments.length !== 1 ? 's' : ''}
          {limit && adjustments.length >= limit && ` (limited to ${limit})`}
        </div>
      </div>
    </div>
  );
}